import { Check, Edit2, Loader2, X } from 'lucide-react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { useSalesPotentialEditor } from 'src/features/novelties/hooks/useSalesPotentialEditor';
import { cn } from 'src/lib/utils';

type SalesPotentialEditorProps = {
  sku: string;
  salesPotential: number | null;
};

export function SalesPotentialEditor({
  sku,
  salesPotential,
}: SalesPotentialEditorProps): ReactElement {
  const { t } = useTranslation('novelties');
  const {
    isEditing,
    editValue,
    isPending,
    saveSalesPotentialAction,
    setEditValue,
    handleEdit,
    handleCancelClick,
    handleInputClick,
    handleKeyDown,
  } = useSalesPotentialEditor({ sku, salesPotential });

  return (
    <div
      className='flex flex-col items-end gap-1'
      data-sales-potential-editor
      onMouseDown={(e) => e.stopPropagation()}
      role='none'
    >
      <span className='text-layout-foreground-secondary text-xs'>
        {t('Sales potential')}
      </span>
      {isEditing ? (
        <form
          action={saveSalesPotentialAction}
          className='flex items-center gap-2'
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
        >
          <Input
            autoFocus
            className='h-8 w-24 text-right text-sm'
            min={0}
            name='salesPotential'
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder='0'
            value={editValue}
          />
          <Button
            className='size-8'
            disabled={isPending}
            size='icon'
            type='submit'
            variant='ghost'
          >
            {isPending ? (
              <Loader2 className='size-4 animate-spin' />
            ) : (
              <Check className='size-4 text-success-default' />
            )}
          </Button>
          <Button
            className='size-8'
            disabled={isPending}
            onClick={handleCancelClick}
            size='icon'
            type='button'
            variant='ghost'
          >
            <X className='size-4 text-error-default' />
          </Button>
        </form>
      ) : (
        <div className='flex items-center gap-2'>
          <span
            className={cn(
              'font-semibold',
              salesPotential === null
                ? 'text-layout-foreground-secondary'
                : 'text-primary-default',
            )}
          >
            {salesPotential !== null ? salesPotential.toLocaleString() : '-'}
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className='size-8'
                  onClick={handleEdit}
                  size='icon'
                  variant='ghost'
                >
                  <Edit2 className='size-4' />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('Edit sales potential')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
}
